interface EcmFieldDisplayProps {
  ecm: number;
  dodgeBonus?: number;
  isEcmModified?: boolean;
  isDodgeModified?: boolean;
  baseEcm?: number;
  baseDodge?: number;
  className?: string;
  /** One row label/value (embedded unit stats rail) */
  compact?: boolean;
}

/**
 * Custom ECM field display that combines ECM and dodge bonus into a single percentage
 * Dodge bonus is added to ECM for a total effectiveness value
 */
export function EcmFieldDisplay({
  ecm,
  dodgeBonus,
  isEcmModified = false,
  isDodgeModified = false,
  baseEcm,
  baseDodge,
  className = "",
  compact = false,
}: EcmFieldDisplayProps) {
  // Convert ECM to positive percentage (ECM is stored as negative)
  const ecmPercent = Math.round(ecm * -100);
  const baseEcmPercent = baseEcm !== undefined ? Math.round(baseEcm * -100) : undefined;
  
  // Convert dodge bonus to positive percentage
  const dodgePercent = dodgeBonus ? Math.round(dodgeBonus * 100) : 0;
  const baseDodgePercent = baseDodge !== undefined ? Math.round(baseDodge * 100) : undefined;
  
  // Combine ECM and dodge bonus into total effective ECM
  const totalEcmPercent = ecmPercent + dodgePercent;
  
  // Determine if anything is modified
  const isModified = isEcmModified || isDodgeModified;
  
  // Build the display string with combined ECM
  const displayValue = `${totalEcmPercent}%`;
  
  // Build the base value string if modified
  let baseDisplayValue: string | undefined;
  if (isModified && (baseEcmPercent !== undefined || baseDodgePercent !== undefined)) {
    const baseEcmDisplay = baseEcmPercent !== undefined ? baseEcmPercent : ecmPercent;
    const baseDodgeDisplay = baseDodgePercent !== undefined ? baseDodgePercent : 0;
    
    // Combine base ECM and base dodge bonus
    const baseTotalEcmPercent = baseEcmDisplay + baseDodgeDisplay;
    baseDisplayValue = `${baseTotalEcmPercent}%`;
  }

  const valueEl = (
    <span className={`text-sm font-semibold ${isModified ? "text-primary" : ""}`}>
      {displayValue}
      {isModified && baseDisplayValue && displayValue !== baseDisplayValue && (
        <span className="text-xs text-muted-foreground ml-1">({baseDisplayValue})</span>
      )}
    </span>
  );

  if (compact) {
    return (
      <div
        className={`grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2 ${className}`}
      >
        <span className="min-w-0 text-xs text-muted-foreground">ECM</span>
        <span className="shrink-0 text-end tabular-nums">{valueEl}</span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-0 ${className}`}>
      <span className="text-xs text-muted-foreground">ECM</span>
      <div className="flex items-center gap-1">{valueEl}</div>
    </div>
  );
}

